import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Database, DownloadSimple, ArrowsClockwise, ListMagnifyingGlass, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDate = (iso, lang) => {
  try { return new Date(iso).toLocaleString(lang === "es" ? "es-PE" : "en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export default function SecurityPanel() {
  const { t, lang } = useLang();
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/security/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/security/audit?limit=50").then((r) => setAudit(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const runBackup = async () => {
    setBusy(true);
    try {
      await api.post("/security/backup");
      toast.success(t("backupDone"));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const restore = async () => {
    if (!window.confirm(t("restoreConfirm"))) return;
    setBusy(true);
    try {
      await api.post("/security/restore/replica");
      toast.success(t("restoreDone"));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const last = status.backup?.last;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-3xl" data-testid="security-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={20} weight="duotone" className="text-emerald-600" />
        <Label className="overline-label">{t("securityPanel")}</Label>
      </div>
      <p className="text-sm text-slate-500 mb-6">{t("securityPanelHint")}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-slate-200 rounded-md p-3" data-testid="security-last-backup">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("lastBackup")}</p>
          <p className="text-xs font-semibold text-slate-800 mt-1">{last ? fmtDate(last.created_at, lang) : t("noBackups")}</p>
          {last && <p className="text-[10px] text-slate-500">{last.documents} {t("docsCount")} · {fmtBytes(last.size_bytes)}</p>}
        </div>
        <div className="border border-slate-200 rounded-md p-3" data-testid="security-replica-info">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("replicaDb")}</p>
          <p className="text-xs font-semibold text-slate-800 mt-1">{status.replica.collections} {t("collectionsCount")}</p>
          <p className="text-[10px] text-slate-500">{status.replica.documents} {t("docsCount")}</p>
        </div>
        <div className="border border-slate-200 rounded-md p-3" data-testid="security-audit-count">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("auditEvents")}</p>
          <p className="text-xs font-semibold text-slate-800 mt-1">{status.audit_events}</p>
        </div>
        <div className="border border-slate-200 rounded-md p-3" data-testid="security-failed-logins">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("failedLogins24h")}</p>
          <p className={`text-xs font-semibold mt-1 ${status.failed_logins_24h > 0 ? "text-red-600" : "text-emerald-600"}`}>{status.failed_logins_24h}</p>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mb-3">{t("nextBackupEvery", { h: status.backup.interval_hours })}</p>
      <div className="flex flex-wrap gap-2 mb-6">
        <Button data-testid="run-backup-button" onClick={runBackup} disabled={busy} className="bg-blue-900 hover:bg-blue-800 gap-2">
          <DownloadSimple size={16} weight="bold" /> {busy ? t("backupRunning") : t("runBackup")}
        </Button>
        <Button data-testid="restore-replica-button" variant="outline" onClick={restore} disabled={busy} className="gap-2 text-amber-700 hover:text-amber-800">
          <ArrowsClockwise size={16} weight="bold" /> {t("restoreReplica")}
        </Button>
      </div>

      {status.backup.files.length > 0 && (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
            <Database size={13} /> {t("backupFiles")}
          </p>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-auto" data-testid="backup-files-list">
            {status.backup.files.map((f) => (
              <div key={f.filename} className="flex justify-between items-center px-3 py-1.5 text-xs">
                <span className="font-mono text-slate-700">{f.filename}</span>
                <span className="text-slate-500">{fmtBytes(f.size_bytes)} · {fmtDate(f.created_at, lang)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
        <ListMagnifyingGlass size={13} /> {t("auditLogTitle")}
      </p>
      <div className="border border-slate-200 rounded-md overflow-auto max-h-64" data-testid="audit-log-table">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr className="text-left text-slate-500">
              <th className="px-3 py-2 font-semibold">{t("eventCol")}</th>
              <th className="px-3 py-2 font-semibold">{t("actorCol")}</th>
              <th className="px-3 py-2 font-semibold">{t("ipCol")}</th>
              <th className="px-3 py-2 font-semibold">{t("detailCol")}</th>
              <th className="px-3 py-2 font-semibold">{t("whenCol")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {audit.map((a, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1">
                    {a.success ? <CheckCircle size={12} weight="fill" className="text-emerald-500" /> : <XCircle size={12} weight="fill" className="text-red-500" />}
                    {a.event}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-slate-700">{a.actor}</td>
                <td className="px-3 py-1.5 font-mono text-slate-500">{a.ip}</td>
                <td className="px-3 py-1.5 text-slate-600 max-w-[200px] truncate" title={a.detail}>{a.detail}</td>
                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{fmtDate(a.created_at, lang)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
